// /signin — the platform's NATIVE sign-in page (no hosted UI, no redirect).
//
// This screen is now a thin RENDERER over the SDK's `app.auth.flow()` machine
// (see packages/sdk/src/auth-flow.ts). It holds no auth logic of its own: it
// reads `state.step` + `state.methods` (the project's published sign-in methods)
// and draws the matching card. Identifier-first, passwordless-promoted, provider
// buttons, the challenge loop (MFA / NEW_PASSWORD_REQUIRED), unverified-account
// recovery, and forgot-password all come from the machine — the exact same one
// the vanilla test app drives. On `step: "done"` the session is already adopted
// live; we do a full navigation to `returnTo` so every provider re-hydrates.

import {
	Alert,
	Anchor,
	Button,
	Card,
	Divider,
	Group,
	Loader,
	PasswordInput,
	Stack,
	Text,
	TextInput,
	Title,
} from "@mantine/core";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Wordmark } from "../components/Footer.tsx";
import { CONSOLE_ORIGIN, MARKETING_ORIGIN, newAppPath, onConsoleHost } from "../lib/hosts.ts";
import { useAuthFlow } from "../session/useAuthFlow.ts";

/** Cognito provider NAMES aren't all button-friendly (e.g. SignInWithApple). */
function providerLabel(name: string): string {
	if (name === "SignInWithApple") return "Apple";
	if (name === "OIDC") return "your organization";
	return name;
}

export function SignIn() {
	const [params] = useSearchParams();
	// Default landing after sign-in depends on WHICH SITE you signed in on: the
	// console host takes you to your apps; the marketing host sends you across to
	// the console. It used to hardcode "/app" — a path that no longer exists on
	// either host, so a plain sign-in dumped you on a 404.
	const returnTo = params.get("returnTo") || (onConsoleHost() ? "/" : CONSOLE_ORIGIN);

	const { flow, state } = useAuthFlow();

	// Local input state — the machine owns the FLOW, the fields stay with the UI.
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [code, setCode] = useState("");
	const [newPassword, setNewPassword] = useState("");
	// Password is the secondary path in `choose`; reveal on demand unless it's
	// the only method.
	const [showPassword, setShowPassword] = useState(false);

	// On success the session is live; full-nav so every provider re-hydrates.
	useEffect(() => {
		if (state?.step === "done") window.location.assign(returnTo);
	}, [state?.step, returnTo]);

	if (!flow || !state) {
		return (
			<Stack align="center" pt={120}>
				<Loader />
			</Stack>
		);
	}

	const { step, methods, busy, error } = state;
	const errorAlert = error ? (
		<Alert color="red" variant="light" title="Couldn't sign in">
			{error}
		</Alert>
	) : null;

	return (
		<Stack gap="xl" maw={440} mx="auto" px="md" py={48}>
			<Stack gap={2}>
				{/* You usually arrive here REDIRECTED, so this may be the first thing you
				    see — it has to say whose login this is. Same <Wordmark/> as the chrome. */}
				<Anchor href={MARKETING_ORIGIN} underline="never" w="fit-content" mb="md">
					<Wordmark size={26} />
				</Anchor>
				<Title order={1}>Sign in</Title>
				<Text c="dimmed" size="sm">
					Your platform account works everywhere: this console, every project you
					operate, and the CLI.
				</Text>
			</Stack>
			<Card withBorder radius="md" padding="lg">
				<Stack>
					{step === "identify" && (
						<>
							{/* Social first — the provider supplies the identity, no email typed. */}
							{methods.providers.map((name) => (
								<Button
									key={name}
									fullWidth
									variant="default"
									loading={busy}
									onClick={() => void flow.useProvider(name)}
								>
									Continue with {providerLabel(name)}
								</Button>
							))}
							{methods.providers.length > 0 && (methods.password || methods.emailCode) && (
								<Divider label="or" labelPosition="center" />
							)}
							{/* Email step only when an email-based method exists — a provider-only
							    app shows no dead email box. */}
							{(methods.password || methods.emailCode) && (
								<form
									onSubmit={(e) => {
										e.preventDefault();
										setShowPassword(!methods.emailCode && methods.providers.length === 0);
										void flow.identify(email);
									}}
								>
									<Stack>
										<TextInput
											label="Email"
											type="email"
											name="email"
											// "username", not "email": password managers offer saved LOGINS
											// only on username-marked fields; "email" gets the contact-card
											// dropdown instead.
											autoComplete="username"
											autoFocus
											placeholder="you@example.com"
											value={email}
											onChange={(e) => setEmail(e.currentTarget.value)}
										/>
										{errorAlert}
										<Button type="submit" fullWidth loading={busy} disabled={!email.trim()}>
											Continue
										</Button>
									</Stack>
								</form>
							)}
							{!(methods.password || methods.emailCode) && errorAlert}
							<Text size="sm" c="dimmed" ta="center">
								New here?{" "}
								<Anchor size="sm" component={Link} to={newAppPath()}>
									Create an account
								</Anchor>
							</Text>
						</>
					)}

					{step === "choose" && (
						<>
							<Group justify="space-between" gap="xs">
								<Text size="sm">
									Signing in as <b>{state.email}</b>
								</Text>
								<Anchor
									size="sm"
									onClick={() => {
										setShowPassword(false);
										flow.back();
									}}
								>
									change
								</Anchor>
							</Group>

							{errorAlert}

							{/* Providers live on step 1 (no email needed). Here: the methods that
							    DO need the identity — email code, then password. */}
							{methods.emailCode && (
								<Button fullWidth onClick={() => void flow.useEmailCode()} loading={busy}>
									Email me a sign-in code
								</Button>
							)}

							{methods.password &&
								!showPassword &&
								(methods.emailCode || methods.providers.length > 0) && (
									<Anchor size="sm" ta="center" onClick={() => setShowPassword(true)}>
										Use your password instead
									</Anchor>
								)}
							{methods.password && showPassword && (
								<form
									onSubmit={(e) => {
										e.preventDefault();
										if (password) void flow.usePassword(password);
									}}
								>
									<Stack>
										<input
											type="email"
											name="email"
											autoComplete="username"
											value={state.email}
											readOnly
											hidden
										/>
										<PasswordInput
											label="Password"
											name="password"
											autoComplete="current-password"
											value={password}
											onChange={(e) => setPassword(e.currentTarget.value)}
											autoFocus
										/>
										<Button type="submit" fullWidth loading={busy} disabled={!password}>
											Sign in
										</Button>
										<Anchor size="sm" ta="center" onClick={() => flow.startReset()}>
											Forgot password?
										</Anchor>
									</Stack>
								</form>
							)}
						</>
					)}

					{/* Single-method password pools skip `choose` and land here. */}
					{step === "password" && (
						<>
							<Group justify="space-between" gap="xs">
								<Text size="sm">
									Signing in as <b>{state.email}</b>
								</Text>
								<Anchor size="sm" onClick={() => flow.back()}>
									change
								</Anchor>
							</Group>
							<form
								onSubmit={(e) => {
									e.preventDefault();
									if (password) void flow.usePassword(password);
								}}
							>
								<Stack>
									<input
										type="email"
										name="email"
										autoComplete="username"
										value={state.email}
										readOnly
										hidden
									/>
									<PasswordInput
										label="Password"
										autoComplete="current-password"
										value={password}
										onChange={(e) => setPassword(e.currentTarget.value)}
										autoFocus
									/>
									{errorAlert}
									<Button type="submit" fullWidth loading={busy} disabled={!password}>
										Sign in
									</Button>
									<Anchor size="sm" ta="center" onClick={() => flow.startReset()}>
										Forgot password?
									</Anchor>
								</Stack>
							</form>
						</>
					)}

					{/* Unverified-account recovery: the machine sent a fresh code and
					    dropped us here; on confirm it retries the original sign-in. */}
					{step === "verify" && (
						<>
							<Text size="sm" c="dimmed">
								That email needs verifying — enter the code we just sent to{" "}
								<b>{state.email}</b> and we'll finish signing you in.
							</Text>
							<TextInput
								label="Verification code"
								placeholder="123456"
								inputMode="numeric"
								value={code}
								onChange={(e) => setCode(e.currentTarget.value)}
							/>
							{errorAlert}
							<Button
								fullWidth
								onClick={() => void flow.confirmSignup(code)}
								loading={busy}
								disabled={!code.trim()}
							>
								Verify &amp; sign in
							</Button>
							<Anchor size="sm" ta="center" onClick={() => void flow.resend()}>
								Send another code
							</Anchor>
						</>
					)}

					{step === "challenge" && (
						<>
							<Text size="sm" c="dimmed">
								{state.challenge === "NEW_PASSWORD_REQUIRED"
									? "Your temporary password must be replaced — choose a new one."
									: `Additional verification required (${state.challenge}). Enter the code.`}
							</Text>
							{state.challenge === "NEW_PASSWORD_REQUIRED" ? (
								<PasswordInput
									label="New password"
									placeholder="At least 10 characters"
									autoComplete="new-password"
									value={newPassword}
									onChange={(e) => setNewPassword(e.currentTarget.value)}
								/>
							) : (
								<TextInput
									label="Code"
									placeholder="123456"
									name="one-time-code"
									autoComplete="one-time-code"
									inputMode="numeric"
									value={code}
									onChange={(e) => setCode(e.currentTarget.value)}
								/>
							)}
							{errorAlert}
							<Button
								fullWidth
								loading={busy}
								disabled={
									state.challenge === "NEW_PASSWORD_REQUIRED"
										? newPassword.length < 10
										: !code.trim()
								}
								onClick={() =>
									void flow.answer(
										state.challenge === "NEW_PASSWORD_REQUIRED" ? newPassword : code,
									)
								}
							>
								Continue
							</Button>
						</>
					)}

					{step === "reset" && state.resetStage === "request" && (
						<>
							<Text size="sm" c="dimmed">
								We'll email a reset code to <b>{state.email}</b>.
							</Text>
							{errorAlert}
							<Button fullWidth onClick={() => void flow.requestReset()} loading={busy}>
								Email me a reset code
							</Button>
							<Anchor size="sm" ta="center" onClick={() => flow.back()}>
								← Back to sign in
							</Anchor>
						</>
					)}

					{step === "reset" && state.resetStage === "confirm" && (
						<>
							<TextInput
								label="Reset code"
								placeholder="123456"
								value={code}
								onChange={(e) => setCode(e.currentTarget.value)}
							/>
							<PasswordInput
								label="New password"
								placeholder="At least 10 characters"
								value={newPassword}
								onChange={(e) => setNewPassword(e.currentTarget.value)}
							/>
							{errorAlert}
							<Button
								fullWidth
								loading={busy}
								disabled={!code.trim() || newPassword.length < 10}
								onClick={() => void flow.submitReset(code, newPassword)}
							>
								Set new password
							</Button>
						</>
					)}
				</Stack>
			</Card>
		</Stack>
	);
}
